import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HOME_TRANSLATIONS } from '../home/home-translations';
import { EmailSubscriptionService } from 'src/app/services/email-subscription.service';
import { OtpService } from 'src/app/services/otp.service';
import { io, Socket } from 'socket.io-client';

@Component({
  selector: 'app-public-footer',
  standalone: true,
  imports: [RouterModule, CommonModule, FormsModule],
  templateUrl: './public-footer.html',
  styleUrl: './public-footer.css',
})
export class PublicFooter implements OnInit, OnDestroy {
  currentLang: 'en' | 'or' = 'or';
  activeUsersCount = 0;
  private socket!: Socket;

  @HostListener('window:langChange', ['$event'])
  onLangChange(event: any) {
    this.currentLang = event.detail;
  }

  // ─── Subscription form state ─────────────────────────────────────────────────
  subscribeEmail = '';
  isLoading = false;
  successMessage = '';
  errorMessage = '';
  private clearTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private emailSubscriptionService: EmailSubscriptionService,
    private otpService: OtpService
  ) {
    const savedLang = localStorage.getItem('app_lang') as 'en' | 'or';
    if (savedLang) {
      this.currentLang = savedLang;
    }
  }

  ngOnInit(): void {
    // 1. Load initial count via REST fallback
    this.loadActiveUsersCount();

    // 2. Establish Socket.io connection for real-time updates
    try {
      const socketUrl = this.otpService.getApiUrl();
      this.socket = io(socketUrl, {
        transports: ['websocket', 'polling']
      });

      this.socket.on('activeUsersCount', (data: { count: number }) => {
        if (data && typeof data.count === 'number') {
          this.activeUsersCount = data.count;
        }
      });

      this.socket.on('connect_error', (err) => {
        console.warn("Socket connection failed, falling back to REST:", err.message);
      });
    } catch (err) {
      console.error("Failed to initialize Socket.io:", err);
    }
  }

  ngOnDestroy(): void {
    if (this.socket) {
      this.socket.disconnect();
    }
  }

  loadActiveUsersCount(): void {
    this.otpService.getActiveUsersCount().subscribe({
      next: (res) => {
        if (res && typeof res.count === 'number') {
          this.activeUsersCount = res.count;
        }
      },
      error: (err) => {
        console.error("Failed to load active users in public footer:", err);
      }
    });
  }

  get t() {
    return (HOME_TRANSLATIONS as any)[this.currentLang];
  }

  // ─── Client-side email validation ────────────────────────────────────────────
  get isEmailInvalid(): boolean {
    return (
      this.subscribeEmail.trim().length > 0 &&
      !this.emailSubscriptionService.validateEmail(this.subscribeEmail)
    );
  }

  // ─── Subscribe handler ───────────────────────────────────────────────────────
  onSubscribe(): void {
    this.successMessage = '';
    this.errorMessage = '';

    const email = this.subscribeEmail.trim();

    if (!email) {
      this.errorMessage = 'Please enter your email address.';
      return;
    }

    if (!this.emailSubscriptionService.validateEmail(email)) {
      // isEmailInvalid getter already shows the inline hint — no errorMessage needed
      return;
    }

    this.isLoading = true;

    this.emailSubscriptionService.subscribe(email).subscribe({
      next: () => {
        this.isLoading = false;
        this.subscribeEmail = '';
        this.successMessage =
          'You have successfully subscribed! Thank you for staying updated.';
        this.autoClearMessages();
      },
      error: (err) => {
        this.isLoading = false;
        const serverMsg: string =
          err?.graphQLErrors?.[0]?.message ||
          err?.message ||
          'Something went wrong. Please try again.';

        if (serverMsg.toLowerCase().includes('already subscribed')) {
          this.errorMessage = 'This email is already subscribed.';
        } else {
          this.errorMessage = serverMsg;
        }
        this.autoClearMessages();
      },
    });
  }

  // ─── Auto-clear messages after 5 s ──────────────────────────────────────────
  private autoClearMessages(): void {
    if (this.clearTimer) clearTimeout(this.clearTimer);
    this.clearTimer = setTimeout(() => {
      this.successMessage = '';
      this.errorMessage = '';
      this.clearTimer = null;
    }, 5000);
  }
}
